<template>
  <div class="images-page"
    @dragenter.prevent="onPageDragEnter"
    @dragover.prevent
    @dragleave.prevent="onPageDragLeave"
    @drop.prevent="onPageDrop"
  >
    <!-- Объединённый toolbar с breadcrumbs -->
    <div class="grid">
      <div class="col-12">
        <Card>
          <template #content>
            <Breadcrumb :model="breadcrumbItems" class="mb-2">
              <template #item="{ item, props }">
                <a href="#" @click.prevent="navigateBreadcrumb(item)" v-bind="props.action">
                  <span class="text-primary font-semibold">{{ item.label }}</span>
                </a>
              </template>
            </Breadcrumb>
            <div class="flex flex-wrap align-items-center justify-content-between gap-3">
              <div class="flex align-items-center gap-2 flex-wrap">
                <Button icon="pi pi-folder-plus" label="Создать папку" @click="showCreateFolderDialog = true" size="small" />
                <Button icon="pi pi-upload" label="Загрузить изображения" @click="showUploadDialog = true" size="small" />
                <Button icon="pi pi-file-upload" label="Загрузить ZIP" @click="showZipUploadDialog = true" size="small" />
                <Button icon="pi pi-sync" label="Синхронизация" @click="showResyncDialog = true" size="small" />

                <!-- Bulk actions -->
                <div v-if="selectedItems.length > 0" class="flex align-items-center gap-2 ml-3">
                  <Divider layout="vertical" />
                  <span class="text-sm text-600">Выбрано: {{ selectedItems.length }}</span>
                  <Button icon="pi pi-trash" label="Удалить выбранные" @click="confirmBulkDelete" severity="danger" size="small" />
                  <Button icon="pi pi-download" label="Скачать ZIP" @click="exportSelected" :loading="exporting" :disabled="selectedImageCount === 0 || exporting" severity="secondary" size="small" />
                  <Button icon="pi pi-times" label="Снять выделение" @click="clearSelection" text size="small" />
                </div>
              </div>

              <div class="flex align-items-center gap-2">
                <!-- Сортировка -->
                <Dropdown v-model="imagesStore.sortBy"
                  :options="[{label:'По имени',value:'name'},{label:'По размеру',value:'size'},{label:'По дате',value:'date'}]"
                  optionLabel="label" optionValue="value" size="small" class="w-9rem" />
                <Button :icon="imagesStore.sortOrder === 'asc' ? 'pi pi-sort-amount-up' : 'pi pi-sort-amount-down'"
                  text size="small" @click="imagesStore.toggleSortOrder()" v-tooltip.bottom="'Порядок сортировки'" />

                <Divider layout="vertical" />

                <!-- Размер миниатюр -->
                <SelectButton v-model="imagesStore.thumbnailSize" :options="['S','M','L','XL']" :allowEmpty="false" size="small" />

                <Divider layout="vertical" />

                <!-- Переключение вида -->
                <div class="flex align-items-center gap-1">
                  <Button icon="pi pi-th-large" @click="imagesStore.viewMode = 'grid'"
                    :class="{ 'p-button-secondary': imagesStore.viewMode !== 'grid' }" size="small" text />
                  <Button icon="pi pi-list" @click="imagesStore.viewMode = 'list'"
                    :class="{ 'p-button-secondary': imagesStore.viewMode !== 'list' }" size="small" text />
                </div>

                <!-- Поиск -->
                <InputText v-model="searchQuery" placeholder="Поиск..." class="w-12rem" size="small">
                  <template #prefix><i class="pi pi-search" /></template>
                </InputText>
              </div>
            </div>
          </template>
        </Card>
      </div>
    </div>

    <!-- Содержимое -->
    <div class="grid">
      <!-- Баннер ошибок -->
      <div v-if="retryState.showBanner" class="col-12">
        <Card class="mb-3">
          <template #content>
            <div class="flex align-items-center gap-3 p-2">
              <i class="pi pi-exclamation-triangle text-orange-500 text-xl"></i>
              <div class="flex-1">
                <div class="font-medium text-900 mb-1">Ошибка загрузки данных</div>
                <div class="text-600 text-sm">{{ retryState.lastError }}</div>
                <div v-if="retryState.count < retryState.maxRetries" class="text-500 text-xs mt-1">
                  Попытка {{ retryState.count + 1 }} из {{ retryState.maxRetries }}.
                  Следующая попытка через {{ Math.ceil((retryState.baseDelay * Math.pow(2, retryState.count)) / 1000) }} сек.
                </div>
                <div v-else class="text-red-500 text-xs mt-1">Превышено максимальное количество попыток</div>
              </div>
              <div class="flex align-items-center gap-2">
                <Button label="Повторить сейчас" size="small" outlined @click="manualRetry" />
                <Button icon="pi pi-times" size="small" text @click="retryState.showBanner = false" v-tooltip.top="'Скрыть уведомление'" />
              </div>
            </div>
          </template>
        </Card>
      </div>

      <div class="col-12">
        <Card>
          <template #content>
            <!-- Режим сетки -->
            <div v-if="imagesStore.viewMode === 'grid'" ref="gridContainer" class="grid-container"
              :style="{ '--thumb-size': imagesStore.thumbnailPx + 'px' }" @mousedown="startSelectionRectangle">

              <!-- Skeleton loading -->
              <div v-if="loading" class="grid">
                <div v-for="n in skeletonCount" :key="'sk-' + n" :class="gridColumnClass">
                  <div class="image-item p-3 border-round">
                    <Skeleton width="100%" :height="imagesStore.thumbnailPx + 'px'" class="mb-2 border-round" />
                    <Skeleton width="75%" height="0.875rem" class="mb-1" />
                    <Skeleton width="40%" height="0.75rem" />
                  </div>
                </div>
              </div>

              <!-- Содержимое -->
              <div v-else class="grid">
                <!-- Кнопка "Назад" -->
                <div v-if="currentPath !== '/'" :class="gridColumnClass">
                  <div class="folder-item p-3 border-round cursor-pointer hover:bg-primary-50 transition-colors" @click="goBack">
                    <div class="text-center">
                      <i class="pi pi-arrow-left text-4xl text-600 mb-2"></i>
                      <div class="text-sm font-medium">Назад</div>
                    </div>
                  </div>
                </div>

                <!-- Папки -->
                <div v-for="folder in filteredFolders" :key="folder.id" :class="gridColumnClass"
                  :ref="el => setItemRef(el, folder, 'folder')">
                  <div class="selectable-item folder-item p-3 border-round cursor-pointer transition-colors"
                    :class="{
                      'bg-primary-100 border-primary': isSelected(folder, 'folder'),
                      'hover:bg-primary-50': !isSelected(folder, 'folder'),
                      'drag-over': dragOverFolderId === folder.id,
                      'dragging': isDragging && isSelected(folder, 'folder')
                    }"
                    @click="handleItemClick(folder, 'folder', $event)"
                    @contextmenu="!isDragging ? showContextMenu($event, folder, 'folder') : null"
                    @dragstart="handleDragStart($event, folder, 'folder')"
                    @dragenter="handleDragEnter($event, folder)"
                    @dragover="handleDragOver"
                    @dragleave="handleDragLeave($event, folder)"
                    @drop="handleDrop($event, folder)"
                    @dragend="handleDragEnd"
                    draggable="true"
                  >
                    <div class="text-center">
                      <div class="relative">
                        <i class="pi pi-folder text-4xl text-orange-500 mb-2"></i>
                        <div v-if="isSelected(folder, 'folder')" class="selection-indicator">✓</div>
                      </div>
                      <div class="text-sm font-medium">{{ folder.name }}</div>
                      <div v-if="folder.itemCount != null" class="text-xs text-500">{{ folder.itemCount }} элементов</div>
                    </div>
                  </div>
                </div>

                <!-- Изображения -->
                <div v-for="(image, imgIdx) in paginatedImages" :key="image.id" :class="gridColumnClass"
                  :ref="el => setItemRef(el, image, 'image')">
                  <div class="selectable-item image-item p-3 border-round cursor-pointer transition-colors"
                    :class="{
                      'bg-primary-100 border-primary': isSelected(image, 'image'),
                      'hover:bg-primary-50': !isSelected(image, 'image'),
                      'dragging': isDragging && isSelected(image, 'image'),
                      'preview-below': imgIdx < previewBelowThreshold
                    }"
                    @click="handleItemClick(image, 'image', $event)"
                    @contextmenu="!isDragging ? showContextMenu($event, image, 'image') : null"
                    @dragstart="handleDragStart($event, image, 'image')"
                    @dragend="handleDragEnd"
                    draggable="true"
                  >
                    <div class="text-center">
                      <div class="relative">
                        <div class="image-thumbnail mb-2">
                          <img :src="image.thumbnailUrl || image.url" :alt="image.name"
                            class="w-full h-full object-cover border-round"
                            loading="lazy" :data-image-id="image.id"
                            @error="handleImageError" @load="handleImageLoad" />
                          <div v-if="imageLoadingStates[image.id]" class="image-loading-overlay">
                            <i class="pi pi-spin pi-spinner"></i>
                          </div>
                        </div>
                        <div v-if="isSelected(image, 'image')" class="selection-indicator">✓</div>
                      </div>
                      <!-- Hover preview -->
                      <div v-if="!isDragging" class="image-hover-preview">
                        <img :src="image.url" :alt="image.name" loading="lazy" />
                      </div>
                      <div class="text-sm font-medium text-overflow-ellipsis">{{ image.name }}</div>
                      <div class="text-xs text-500">{{ formatFileSize(image.size) }}</div>
                    </div>
                  </div>
                </div>

                <!-- Enhanced empty state -->
                <div v-if="filteredFolders.length === 0 && sortedImages.length === 0" class="col-12">
                  <div class="flex flex-column align-items-center justify-content-center p-6">
                    <i class="pi pi-images text-6xl text-300 mb-4"></i>
                    <div class="text-xl font-medium text-700 mb-2">Папка пуста</div>
                    <div class="text-600 mb-4">
                      {{ searchQuery ? 'По запросу ничего не найдено' : 'Загрузите изображения или создайте папку' }}
                    </div>
                    <div v-if="!searchQuery" class="flex gap-2">
                      <Button icon="pi pi-upload" label="Загрузить" @click="showUploadDialog = true" />
                      <Button icon="pi pi-folder-plus" label="Создать папку" @click="showCreateFolderDialog = true" outlined />
                    </div>
                    <Button v-else icon="pi pi-times" label="Сбросить поиск" @click="searchQuery = ''" outlined />
                  </div>
                </div>
              </div>

              <!-- Прямоугольник выделения -->
              <div v-if="selectionRectangle.active" class="selection-rectangle" :style="selectionRectangleStyle"></div>
            </div>

            <!-- Режим списка -->
            <template v-else>
              <div class="list-virtual-wrapper">
                <div class="list-header">
                  <div class="list-cell checkbox-cell"></div>
                  <div class="list-cell icon-cell"></div>
                  <div class="list-cell name-cell">Название</div>
                  <div class="list-cell size-cell">Размер</div>
                  <div class="list-cell updated-cell">Изменено</div>
                  <div class="list-cell actions-cell"></div>
                </div>
                <div v-if="!loading && allItems.length === 0" class="list-empty">
                  <i class="pi pi-folder-open text-4xl text-500 mb-2"></i>
                  <div>Папка пуста</div>
                </div>
                <RecycleScroller
                  v-else
                  class="list-virtual-scroller"
                  :items="allItems"
                  :item-size="68"
                  :buffer="200"
                  key-field="virtualKey"
                >
                  <template #default="{ item }">
                    <div class="list-row"
                      :class="{ 'is-selected': isSelected(item, item.type || 'image') }"
                      @click="handleListRowClick(item, $event)"
                      @contextmenu="!isDragging ? showContextMenu($event, item, item.type || 'image') : null"
                    >
                      <div class="list-cell checkbox-cell">
                        <Checkbox v-if="item.type !== 'back'"
                          :modelValue="isSelected(item, item.type || 'image')" binary
                          @update:modelValue="() => toggleSelection(item, item.type || 'image')"
                          @click.stop />
                      </div>
                      <div class="list-cell icon-cell">
                        <i v-if="item.type === 'folder'" class="pi pi-folder text-orange-500"></i>
                        <img v-else-if="item.type === 'image'" :src="item.thumbnailUrl || item.url" class="w-2rem h-2rem object-cover border-round" />
                        <i v-else class="pi pi-arrow-left"></i>
                      </div>
                      <div class="list-cell name-cell">
                        <span class="cursor-pointer hover:text-primary">{{ item.name }}</span>
                      </div>
                      <div class="list-cell size-cell">
                        <span v-if="item.type === 'image'">{{ formatFileSize(item.size) }}</span>
                        <span v-else-if="item.type === 'folder' && item.itemCount != null">{{ item.itemCount }} элементов</span>
                        <span v-else>—</span>
                      </div>
                      <div class="list-cell updated-cell">
                        <span v-if="item.updatedAt">{{ formatDate(item.updatedAt) }}</span>
                        <span v-else>—</span>
                      </div>
                      <div class="list-cell actions-cell">
                        <Button icon="pi pi-ellipsis-v" text size="small" class="list-action"
                          @click.stop="showContextMenu($event, item, item.type || 'image')" />
                      </div>
                    </div>
                  </template>
                </RecycleScroller>
              </div>
            </template>
          </template>
        </Card>
      </div>
    </div>

    <!-- Статусная панель -->
    <div v-if="(filteredFolders.length > 0 || sortedImages.length > 0) && imagesStore.viewMode === 'grid'" class="grid">
      <div class="col-12">
        <Card>
          <template #content>
            <div class="flex align-items-center justify-content-between text-sm text-600 mb-2">
              <div class="flex align-items-center gap-4">
                <span>{{ filteredFolders.length }} папок, {{ sortedImages.length }} / {{ totalItems }} изображений</span>
                <span v-if="totalPages > 1" class="text-primary">
                  Страница {{ currentPage }}/{{ totalPages }} (показано {{ paginationInfo.start }}-{{ paginationInfo.end }})
                </span>
                <span v-if="selectedItems.length > 0">Выбрано {{ selectedItems.length }} элементов</span>
                <span v-if="!reachedEnd" class="text-orange-600">Загружено {{ images.length }} из {{ totalItems }}</span>
                <span v-else-if="totalItems > 0" class="text-green-600">Загружены все {{ totalItems }} изображений</span>
              </div>
              <div v-if="selectedItems.length > 0" class="flex align-items-center gap-2">
                <Button label="Выбрать все" @click="handleSelectAll" text size="small" class="p-0" />
                <span class="text-300">&bull;</span>
                <Button label="Снять выделение" @click="clearSelection" text size="small" class="p-0" />
              </div>
            </div>

            <!-- Пагинатор -->
            <div v-if="totalPages > 1" class="flex align-items-center justify-content-center gap-2">
              <Button icon="pi pi-angle-double-left" text size="small" @click="currentPage = 1" :disabled="currentPage === 1" v-tooltip.top="'Первая страница'" />
              <Button icon="pi pi-angle-left" text size="small" @click="currentPage = Math.max(1, currentPage - 1)" :disabled="currentPage === 1" v-tooltip.top="'Предыдущая страница'" />
              <div class="flex align-items-center gap-1">
                <template v-for="page in visiblePages" :key="page">
                  <Button v-if="page !== '...'" :label="String(page)" :outlined="page !== currentPage" :text="page !== currentPage"
                    size="small" @click="currentPage = page" class="w-2rem h-2rem p-0"
                    :class="{ 'bg-primary text-primary-contrast': page === currentPage }" />
                  <span v-else class="px-2">...</span>
                </template>
              </div>
              <Button icon="pi pi-angle-right" text size="small" @click="currentPage = Math.min(totalPages, currentPage + 1)" :disabled="currentPage === totalPages" v-tooltip.top="'Следующая страница'" />
              <Button icon="pi pi-angle-double-right" text size="small" @click="currentPage = totalPages" :disabled="currentPage === totalPages" v-tooltip.top="'Последняя страница'" />
              <Divider layout="vertical" />
              <Dropdown v-model="imagesStore.itemsPerPage" :options="[50, 100, 200, 500]" size="small" @change="currentPage = 1" class="w-7rem" />
              <span class="text-xs">на странице</span>
            </div>

            <!-- Lazy loading -->
            <div v-if="!reachedEnd || fetchingMore" class="flex align-items-center justify-content-center gap-3 mt-4 p-3 border-round surface-50">
              <ProgressSpinner v-if="fetchingMore" style="width: 24px; height: 24px" strokeWidth="3" fill="transparent" />
              <Button v-if="!reachedEnd && !fetchingMore" icon="pi pi-download" label="Загрузить ещё" @click="fetchNextPage" size="small" outlined />
              <span class="text-sm text-600">
                <template v-if="fetchingMore">Загрузка следующего блока...</template>
                <template v-else-if="!reachedEnd">Ещё {{ Math.max(0, totalItems - images.length) }} изображений доступно</template>
              </span>
            </div>
          </template>
        </Card>
      </div>
    </div>

    <!-- OS Drop zone overlay -->
    <div v-if="isPageDragOver" class="page-drop-zone">
      <div class="drop-zone-content">
        <i class="pi pi-cloud-upload text-6xl mb-3"></i>
        <div class="text-xl font-medium mb-2">Перетащите файлы сюда</div>
        <div class="text-600">Изображения будут загружены в {{ currentPath }}</div>
      </div>
    </div>
  </div>

  <!-- Диалог пересинхронизации -->
  <Dialog v-model:visible="showResyncDialog" modal header="Синхронизация папок" :style="{ width: '640px' }">
    <div class="flex flex-column gap-3">
      <div class="text-sm text-600">
        Выберите папки, которые нужно пересканировать, чтобы обновить метаданные изображений после изменений на S3.
      </div>
      <FolderPicker v-model:path="resyncFolderPath" :showCheckboxes="true"
        v-model:selectedPaths="resyncSelectedFolders" :showAddButton="true" />
      <div v-if="resyncSelectedFolders.length > 0" class="resync-selected-list">
        <div class="text-sm text-600 mb-2">Выбранные папки:</div>
        <div class="resync-selected-chips">
          <span v-for="path in resyncSelectedFolders" :key="path" class="resync-chip">
            {{ path }}
            <i class="pi pi-times resync-chip-remove" @click="removeResyncSelection(path)"></i>
          </span>
        </div>
        <div v-if="resyncSelectedFolders.includes('/')" class="text-xs text-orange-600 mt-2">
          Синхронизация корневой папки может занять продолжительное время.
        </div>
      </div>
      <div class="flex align-items-center gap-2">
        <Checkbox inputId="resync-clear-existing" v-model="resyncClearExisting" binary />
        <label for="resync-clear-existing" class="text-sm cursor-pointer">Удалять старые записи перед синхронизацией</label>
      </div>
    </div>
    <template #footer>
      <Button label="Отмена" text @click="showResyncDialog = false" :disabled="resyncSubmitting" />
      <Button label="Начать" icon="pi pi-play" @click="startResync" :loading="resyncSubmitting"
        :disabled="resyncSelectedFolders.length === 0 || resyncSubmitting" />
    </template>
  </Dialog>

  <!-- Диалог подтверждения множественного удаления -->
  <Dialog v-model:visible="showBulkDeleteDialog" modal header="Подтверждение удаления" :style="{ width: '400px' }">
    <div class="flex align-items-center gap-3 mb-3">
      <i class="pi pi-exclamation-triangle text-orange-500 text-2xl"></i>
      <div>
        <div class="font-medium text-900 mb-1">Вы уверены, что хотите удалить {{ selectedItems.length }} элементов?</div>
        <div class="text-600 text-sm">Это действие нельзя отменить.</div>
      </div>
    </div>
    <template #footer>
      <Button label="Отмена" text @click="showBulkDeleteDialog = false" />
      <Button label="Удалить" severity="danger" @click="performBulkDelete" :loading="deletingItems" />
    </template>
  </Dialog>

  <!-- Диалог создания папки -->
  <Dialog v-model:visible="showCreateFolderDialog" modal header="Создать папку" :style="{ width: '400px' }">
    <div class="flex flex-column gap-3">
      <div>
        <label for="folderName" class="block text-900 font-medium mb-2">Название папки</label>
        <InputText id="folderName" v-model="newFolderName" placeholder="Введите название..." class="w-full" @keyup.enter="createFolder" />
      </div>
    </div>
    <template #footer>
      <Button label="Отмена" text @click="showCreateFolderDialog = false" />
      <Button label="Создать" @click="createFolder" :disabled="!newFolderName.trim()" />
    </template>
  </Dialog>

  <!-- Диалог загрузки изображений -->
  <Dialog v-model:visible="showUploadDialog" modal header="Загрузить изображения" :style="{ width: '500px' }">
    <div class="flex flex-column gap-3">
      <div class="flex flex-column gap-2">
        <div class="text-600 text-sm">Папка загрузки</div>
        <FolderPicker v-model:path="uploadFolderPath" />
      </div>
      <div class="flex align-items-center gap-2 text-sm text-600">Проверка и замена дубликатов: всегда включено</div>
      <div v-if="isShallowPath(uploadFolderPath)" class="p-3 border-round bg-red-50 text-red-700 text-sm flex align-items-center gap-2">
        <i class="pi pi-exclamation-triangle"></i>
        Загрузка файлов в эту папку запрещена. Выберите подпапку (например, папку бренда).
      </div>
      <div class="flex flex-column gap-2">
        <input ref="imageFileInput" type="file" multiple accept="image/*" @change="onImageFilesChange" style="display: none" />
        <Button icon="pi pi-plus" label="Выбрать изображения" @click="$refs.imageFileInput.click()" outlined :disabled="uploading" />
      </div>
      <div v-if="selectedImages.length > 0" class="flex flex-column gap-2">
        <div class="flex align-items-center justify-content-between">
          <span class="text-sm text-600">Выбрано файлов: {{ selectedImages.length }}</span>
          <Button icon="pi pi-times" label="Очистить" text size="small" @click="clearSelectedImages" :disabled="uploading" />
        </div>
        <div class="selected-files-list border-1 surface-border border-round p-2" style="max-height: 200px; overflow-y: auto;">
          <div v-for="(file, index) in selectedImages" :key="index"
            class="flex align-items-center justify-content-between py-1 px-2 hover:surface-hover border-round">
            <div class="flex align-items-center gap-2">
              <i class="pi pi-image text-primary"></i>
              <span class="text-sm" style="word-break: break-all;">{{ file.name }}</span>
            </div>
            <div class="flex align-items-center gap-2">
              <span class="text-xs text-500">{{ formatFileSize(file.size) }}</span>
              <Button icon="pi pi-times" text rounded size="small" severity="danger" @click="removeSelectedImage(index)" :disabled="uploading" />
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-center p-4 surface-100 border-round">
        <i class="pi pi-cloud-upload text-4xl text-400"></i>
        <div class="text-600 mt-2">Нажмите кнопку выше, чтобы выбрать изображения</div>
      </div>
      <div v-if="uploading && imageUploadProgress > 0" class="mt-3">
        <div class="flex align-items-center justify-content-between mb-1">
          <span class="text-sm text-600">Загрузка файлов...</span>
          <span class="text-sm font-semibold">{{ imageUploadProgress }}%</span>
        </div>
        <ProgressBar :value="imageUploadProgress" :showValue="false" style="height: 6px" />
      </div>
    </div>
    <template #footer>
      <Button label="Отмена" text @click="showUploadDialog = false" :disabled="uploading" />
      <Button label="Загрузить" icon="pi pi-upload" @click="doUploadImages"
        :disabled="selectedImages.length === 0 || uploading || isShallowPath(uploadFolderPath)" :loading="uploading" />
    </template>
  </Dialog>

  <!-- Диалог загрузки ZIP -->
  <Dialog v-model:visible="showZipUploadDialog" modal header="Загрузить ZIP архив"
    :style="{ width: '600px' }" :closable="!uploading" :closeOnEscape="!uploading">
    <div class="flex flex-column gap-3">
      <div class="flex flex-column gap-2">
        <div class="text-600 text-sm">Папка загрузки</div>
        <FolderPicker v-model:path="zipFolderPath" :disabled="uploading" />
      </div>
      <FileUpload ref="zipUpload" mode="basic" accept=".zip" chooseLabel="Выбрать ZIP файл" @select="onZipSelect" :disabled="uploading" />
      <div class="text-xs text-500 mt-2 mb-2">
        <i class="pi pi-info-circle mr-1"></i>
        Поддерживаются только изображения (jpg, png, webp и др.)
      </div>
      <div class="flex align-items-center gap-2 mb-3">
        <Checkbox v-model="zipForceOverwrite" :binary="true" inputId="zipForceOverwrite" :disabled="uploading" />
        <label for="zipForceOverwrite" class="text-sm cursor-pointer">Перезаписать все файлы без проверки дубликатов</label>
      </div>
      <div v-if="uploading || uploadFinished" class="flex flex-column gap-3">
        <div class="upload-stages">
          <div v-for="stage in uploadStages" :key="stage.key" class="upload-stage-item flex align-items-start gap-2 mb-2">
            <div class="upload-stage-icon flex-shrink-0 mt-1">
              <i v-if="stage.completed" class="pi pi-check-circle text-green-500" style="font-size: 1.1rem"></i>
              <i v-else-if="stage.active" class="pi pi-spin pi-spinner text-primary" style="font-size: 1.1rem"></i>
              <i v-else class="pi pi-circle text-300" style="font-size: 1.1rem"></i>
            </div>
            <div class="flex-1" :class="{ 'opacity-40': !stage.active && !stage.completed }">
              <div class="flex align-items-center justify-content-between">
                <span class="text-sm font-medium" :class="stage.active ? 'text-primary' : (stage.completed ? 'text-700' : 'text-400')">{{ stage.label }}</span>
                <span v-if="stage.active && stage.progress > 0 && stage.progress < 100" class="text-xs text-primary font-medium">{{ Math.round(stage.progress) }}%</span>
              </div>
              <div v-if="stage.detail && (stage.active || stage.completed)" class="text-xs mt-1" :class="stage.active ? 'text-600' : 'text-400'">{{ stage.detail }}</div>
              <ProgressBar v-if="stage.active && stage.key === 'uploading' && stage.progress > 0" :value="Math.round(stage.progress)" :showValue="false" class="mt-1" style="height: 4px" />
            </div>
          </div>
        </div>
        <div v-if="currentUploadStage === 'uploading'" class="flex align-items-center justify-content-between px-2 py-1 surface-100 border-round text-xs">
          <div class="flex align-items-center gap-3">
            <span v-if="uploadStats.speed > 0" class="text-600"><i class="pi pi-bolt text-primary mr-1"></i>{{ uploadStats.speed }} файлов/сек</span>
            <span v-if="uploadProgress.total > 0" class="text-600">{{ uploadProgress.processed }} / {{ uploadProgress.total }}</span>
          </div>
          <span v-if="uploadStats.eta > 0" class="text-600"><i class="pi pi-clock mr-1"></i>~{{ formatDuration(uploadStats.eta) }}</span>
        </div>
        <div v-if="uploadProgress.uploaded > 0 || uploadProgress.failed > 0" class="flex align-items-center gap-3 px-2 text-xs">
          <span class="text-green-600 font-medium"><i class="pi pi-check mr-1"></i>{{ uploadProgress.uploaded }} загружено</span>
          <span v-if="uploadProgress.replaced > 0" class="text-orange-500 font-medium"><i class="pi pi-refresh mr-1"></i>{{ uploadProgress.replaced }} обновлено</span>
          <span v-if="uploadProgress.skipped > 0" class="text-500"><i class="pi pi-minus-circle mr-1"></i>{{ uploadProgress.skipped }} без изменений</span>
          <span v-if="uploadProgress.failed > 0" class="text-red-500 font-medium"><i class="pi pi-times mr-1"></i>{{ uploadProgress.failed }} ошибок</span>
        </div>
        <div v-if="uploadProgress.errors.length > 0" class="mt-1">
          <div class="text-xs text-600 mb-1">Последние ошибки:</div>
          <div class="max-h-6rem overflow-auto">
            <div class="text-xs text-red-600 mb-1" v-for="error in uploadProgress.errors.slice(0, 5)" :key="error.filename">{{ error.filename }}: {{ error.error }}</div>
          </div>
        </div>
        <div v-if="uploadFinished" class="border-round surface-border border-1 surface-100 p-3">
          <div class="text-sm text-700">{{ uploadResult ? uploadResult.message : 'Загрузка завершена' }}</div>
          <div v-if="uploadResult && uploadResult.status === 'warn'" class="text-xs text-orange-600 mt-2">Некоторые файлы не загрузились, детали выше.</div>
          <div v-if="uploadResult && uploadResult.status === 'error'" class="text-xs text-red-600 mt-2">Загрузка не выполнена, проверьте сообщение об ошибке.</div>
        </div>
      </div>
    </div>
    <template #footer>
      <template v-if="uploadFinished">
        <Button label="Окей" @click="doAcknowledgeUploadResult" class="p-button-success" />
      </template>
      <template v-else>
        <Button label="Отмена" text @click="doCancelZipUpload" :disabled="uploading && !canCancelUpload" />
        <Button label="Загрузить" @click="doUploadZip" :disabled="!selectedZipFile || uploading" :loading="uploading" />
      </template>
    </template>
  </Dialog>

  <!-- Контекстное меню -->
  <ContextMenu ref="contextMenu" :model="contextMenuItems" />

  <!-- ImageViewer -->
  <ImageViewer
    v-model:visible="showImageDialog"
    :images="paginatedImages"
    :initialIndex="currentImageIndex"
    @delete="(img) => { showImageDialog = false; deleteItem({...img, type:'image'}) }"
    @rename="(img) => { showImageDialog = false; renameItem({...img, type:'image'}) }"
    @download="downloadImage"
  />

  <!-- Диалог переименования -->
  <Dialog v-model:visible="showRenameDialog" modal header="Переименовать" :style="{ width: '400px' }">
    <div class="flex flex-column gap-3">
      <div>
        <label for="newItemName" class="block text-900 font-medium mb-2">
          Новое название {{ itemToRename?.type === 'folder' ? 'папки' : 'файла' }}
        </label>
        <InputText id="newItemName" v-model="newItemName" placeholder="Введите новое название..." class="w-full" @keyup.enter="confirmRename" />
      </div>
    </div>
    <template #footer>
      <Button label="Отмена" text @click="showRenameDialog = false" />
      <Button label="Переименовать" @click="confirmRename" :disabled="!newItemName.trim()" />
    </template>
  </Dialog>

  <!-- Диалог подтверждения удаления -->
  <Dialog v-model:visible="showDeleteDialog" modal header="Подтверждение удаления" :style="{ width: '400px' }">
    <div class="flex align-items-center gap-3 mb-3">
      <i class="pi pi-exclamation-triangle text-orange-500 text-2xl"></i>
      <div>
        <div class="font-medium text-900 mb-1">
          Вы уверены, что хотите удалить {{ itemToDelete?.type === 'folder' ? 'папку' : 'изображение' }} "{{ itemToDelete?.name }}"?
        </div>
        <div class="text-600 text-sm" v-if="itemToDelete?.type === 'folder'">Будут удалены все файлы внутри папки. Это действие нельзя отменить.</div>
        <div class="text-600 text-sm" v-else>Это действие нельзя отменить.</div>
      </div>
    </div>
    <template #footer>
      <Button label="Отмена" text :disabled="deletingItem" @click="showDeleteDialog = false" />
      <Button label="Удалить" severity="danger" :loading="deletingItem" :disabled="deletingItem" @click="confirmDelete" />
    </template>
  </Dialog>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useToast } from 'primevue/usetoast'
import Card from 'primevue/card'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Dialog from 'primevue/dialog'
import FileUpload from 'primevue/fileupload'
import ContextMenu from 'primevue/contextmenu'
import Breadcrumb from 'primevue/breadcrumb'
import ProgressBar from 'primevue/progressbar'
import ProgressSpinner from 'primevue/progressspinner'
import Checkbox from 'primevue/checkbox'
import Divider from 'primevue/divider'
import Dropdown from 'primevue/dropdown'
import SelectButton from 'primevue/selectbutton'
import Skeleton from 'primevue/skeleton'
import { RecycleScroller } from 'vue-virtual-scroller'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'

import apiService from '../services/apiService'
import { formatFileSize, formatDate } from '../utils/dateHelpers'
import { useImagesStore } from '../stores/imagesStore'
import { useFolderNavigation } from '../composables/useFolderNavigation'
import { useImageSelection } from '../composables/useImageSelection'
import { useDragDrop } from '../composables/useDragDrop'
import { useImageUpload } from '../composables/useImageUpload'
import FolderPicker from '../components/FolderPicker.vue'
import ImageViewer from '../components/ImageViewer.vue'

const imagesStore = useImagesStore()
const toast = useToast()
const gridContainer = ref(null)

// ==================== Composables ====================

const {
  currentPath, loading, folders, images, breadcrumbItems,
  searchQuery, filteredFolders, filteredImages,
  reachedEnd, fetchingMore, totalItems,
  retryState, imageLoadingStates,
  loadData, fetchNextPage, openFolder, goBack,
  navigateBreadcrumb, refreshData, manualRetry,
  handleImageError, handleImageLoad
} = useFolderNavigation(toast)

const {
  selectedItems, selectedImageCount,
  selectionRectangle, selectionRectangleStyle,
  isSelected, toggleSelection, clearSelection,
  selectRange, selectAll, setItemRef,
  startSelectionRectangle
} = useImageSelection(gridContainer, { folders, images, filteredFolders, filteredImages })

const {
  isDragging, dragOverFolderId,
  handleDragStart, handleDragOver, handleDrop,
  handleDragEnter, handleDragLeave, handleDragEnd
} = useDragDrop(selectedItems, isSelected, clearSelection, { onMoveItems: moveItemsToFolder })

const {
  selectedImages, uploading, imageUploadProgress,
  onImageFilesChange, clearSelectedImages, removeSelectedImage, uploadImages,
  selectedZipFile, zipForceOverwrite, uploadProgress, uploadStages,
  currentUploadStage, uploadStatus, canCancelUpload, uploadFinished, uploadResult, uploadStats,
  onZipSelect, uploadZip, cancelZipUpload, acknowledgeUploadResult, resetUploadState,
  formatDuration, isShallowPath
} = useImageUpload(toast)

// ==================== Sorting ====================

const sortedImages = computed(() => {
  const arr = [...filteredImages.value]
  const { sortBy, sortOrder } = imagesStore
  arr.sort((a, b) => {
    let cmp = 0
    if (sortBy === 'name') cmp = (a.name || '').localeCompare(b.name || '', 'ru')
    else if (sortBy === 'size') cmp = (a.size || 0) - (b.size || 0)
    else if (sortBy === 'date') cmp = new Date(a.updatedAt || 0) - new Date(b.updatedAt || 0)
    return sortOrder === 'desc' ? -cmp : cmp
  })
  return arr
})

// ==================== Pagination ====================

const currentPage = ref(1)
const MAX_VISIBLE_PAGES = 5

const paginatedImages = computed(() => {
  const start = (currentPage.value - 1) * imagesStore.itemsPerPage
  return sortedImages.value.slice(start, start + imagesStore.itemsPerPage)
})

const totalPages = computed(() => Math.ceil(sortedImages.value.length / imagesStore.itemsPerPage))

const paginationInfo = computed(() => {
  const total = sortedImages.value.length
  const start = (currentPage.value - 1) * imagesStore.itemsPerPage + 1
  const end = Math.min(currentPage.value * imagesStore.itemsPerPage, total)
  return { start, end, total }
})

const allItems = computed(() => {
  const items = []
  if (currentPath.value !== '/') {
    items.push({ type: 'back', name: '...', icon: 'pi pi-arrow-left', virtualKey: 'back' })
  }
  filteredFolders.value.forEach(folder => {
    items.push({ ...folder, type: 'folder', virtualKey: `folder-${folder.id || folder.path}` })
  })
  sortedImages.value.forEach(image => {
    items.push({ ...image, type: 'image', virtualKey: `image-${image.id || image.path}` })
  })
  return items
})

// ==================== Grid layout ====================

const gridColumnClass = computed(() => {
  const map = {
    S:  'col-4 md:col-3 lg:col-2 xl:col-1',
    M:  'col-6 md:col-4 lg:col-3 xl:col-2',
    L:  'col-6 md:col-4 lg:col-3 xl:col-2',
    XL: 'col-6 md:col-6 lg:col-4 xl:col-3'
  }
  return map[imagesStore.thumbnailSize] || map.M
})

const skeletonCount = computed(() => Math.min(18, imagesStore.itemsPerPage))

const previewBelowThreshold = computed(() => {
  const colsMap = { S: 12, M: 6, L: 6, XL: 4 }
  const cols = Math.floor(12 / (colsMap[imagesStore.thumbnailSize] || 6))
  return cols * 2
})

// ==================== Dialogs ====================

const showCreateFolderDialog = ref(false)
const showUploadDialog = ref(false)
const showZipUploadDialog = ref(false)
const showResyncDialog = ref(false)
const showImageDialog = ref(false)
const showRenameDialog = ref(false)
const showDeleteDialog = ref(false)
const showBulkDeleteDialog = ref(false)

const newFolderName = ref('')
const selectedImageForView = ref(null)
const exporting = ref(false)
const deletingItems = ref(false)
const itemToRename = ref(null)
const newItemName = ref('')
const itemToDelete = ref(null)
const deletingItem = ref(false)

// Folder picker paths
const uploadFolderPath = ref('/')
const zipFolderPath = ref('/')
const resyncFolderPath = ref('/')
const resyncSelectedFolders = ref([])
const resyncSubmitting = ref(false)
const resyncClearExisting = ref(true)

// Context menu
const contextMenu = ref()
const contextMenuItems = ref([])

// OS drag & drop
const isPageDragOver = ref(false)
let dragEnterCount = 0

// ==================== Watchers ====================

watch(showUploadDialog, (v) => {
  if (v) { uploadFolderPath.value = currentPath.value } else { selectedImages.value = [] }
})

watch(showZipUploadDialog, (v) => {
  if (v) { zipFolderPath.value = currentPath.value } else { selectedZipFile.value = null; resetUploadState() }
})

watch(showResyncDialog, (visible) => {
  if (visible) {
    resyncFolderPath.value = currentPath.value || '/'
    resyncSelectedFolders.value = []
    resyncClearExisting.value = true
  } else {
    resyncSelectedFolders.value = []
  }
})

watch(currentPath, () => {
  clearSelection()
  currentPage.value = 1
})

watch(searchQuery, () => {
  currentPage.value = 1
})

// Auto-fetch when approaching end
watch([currentPage, () => imagesStore.itemsPerPage, () => images.value.length], () => {
  const visibleEndIndex = currentPage.value * imagesStore.itemsPerPage
  const threshold = images.value.length - Math.ceil(imagesStore.itemsPerPage * 0.5)
  if (visibleEndIndex >= threshold && !reachedEnd.value) {
    fetchNextPage()
  }
})

// Validate selections on data changes
watch(() => images.value.length, (newLength, oldLength) => {
  if (oldLength > 0 && newLength > oldLength) {
    const hasInvalid = selectedItems.value.some(item => {
      if (item.type === 'image') return !images.value.find(img => img.id === item.id)
      return false
    })
    if (hasInvalid) {
      selectedItems.value = selectedItems.value.filter(item => {
        if (item.type === 'image') return images.value.find(img => img.id === item.id)
        return folders.value.find(folder => folder.id === item.id)
      })
    }
  }
})

// ==================== Image viewer ====================

const currentImageIndex = computed(() => {
  if (!selectedImageForView.value || !paginatedImages.value) return -1
  return paginatedImages.value.findIndex(img => img.id === selectedImageForView.value.id)
})

const openImage = (image) => {
  selectedImageForView.value = image
  showImageDialog.value = true
}

// ==================== Item actions ====================

const handleItemClick = (item, type, event) => {
  if (event.ctrlKey || event.metaKey) {
    toggleSelection(item, type)
  } else if (event.shiftKey && selectedItems.value.length > 0) {
    selectRange(item, type)
  } else {
    if (isSelected(item, type) && selectedItems.value.length > 1) return
    if (selectedItems.value.length === 0 || !event.shiftKey) {
      clearSelection()
      if (type === 'folder') openFolder(item)
      else openImage(item)
    }
  }
}

const handleListRowClick = (item, event) => {
  const itemType = item.type || 'image'
  if (itemType === 'back') { goBack(); return }
  handleItemClick(item, itemType, event)
}

const handleSelectAll = () => {
  selectAll()
  if (!reachedEnd.value) {
    toast.add({
      severity: 'info',
      summary: 'Выбраны только загруженные элементы',
      detail: `Выбрано ${selectedItems.value.length} загруженных элементов. Всего в папке ${totalItems.value} элементов.`,
      life: 4000
    })
  }
}

// ==================== CRUD Operations ====================

const validateFolderName = (name) => {
  if (!name || !name.trim()) return 'Имя папки не может быть пустым'
  if (name.includes('/') || name.includes('\\')) return 'Имя папки не может содержать слеши'
  if (name.includes('..')) return 'Имя папки не может содержать ".."'
  if (name.includes('\0')) return 'Некорректное имя папки'
  if (name.trim().length > 255) return 'Имя папки слишком длинное (максимум 255 символов)'
  return null
}

const createFolder = async () => {
  if (!newFolderName.value.trim()) return
  const nameError = validateFolderName(newFolderName.value)
  if (nameError) { toast.add({ severity: 'warn', summary: 'Ошибка', detail: nameError, life: 3000 }); return }
  try {
    await apiService.createFolder(currentPath.value, newFolderName.value.trim())
    toast.add({ severity: 'success', summary: 'Успех', detail: 'Папка создана' })
    newFolderName.value = ''
    showCreateFolderDialog.value = false
    refreshData()
  } catch (error) {
    console.error('Ошибка создания папки:', error)
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось создать папку' })
  }
}

const doUploadImages = () => {
  const target = uploadFolderPath.value || currentPath.value
  uploadImages(target, refreshData, () => { showUploadDialog.value = false })
}

const doUploadZip = () => {
  const target = zipFolderPath.value || currentPath.value
  uploadZip(target, refreshData)
}

const doCancelZipUpload = () => {
  cancelZipUpload(() => { showZipUploadDialog.value = false })
}

const doAcknowledgeUploadResult = () => {
  acknowledgeUploadResult(() => { showZipUploadDialog.value = false })
}

const renameItem = (item) => {
  itemToRename.value = item
  newItemName.value = item.name
  showRenameDialog.value = true
}

const confirmRename = async () => {
  if (!itemToRename.value || !newItemName.value.trim()) return
  const nameError = validateFolderName(newItemName.value)
  if (nameError) { toast.add({ severity: 'warn', summary: 'Ошибка', detail: nameError, life: 3000 }); return }
  try {
    if (itemToRename.value.type === 'folder') {
      await apiService.renameFolder(itemToRename.value.path, newItemName.value.trim())
      toast.add({ severity: 'success', summary: 'Успех', detail: 'Папка переименована' })
    } else {
      await apiService.renameImage(itemToRename.value.path, newItemName.value.trim())
      toast.add({ severity: 'success', summary: 'Успех', detail: 'Изображение переименовано' })
    }
    showRenameDialog.value = false
    itemToRename.value = null
    newItemName.value = ''
    refreshData()
  } catch (error) {
    console.error('Ошибка переименования:', error)
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось переименовать' })
  }
}

const deleteItem = (item) => {
  itemToDelete.value = item
  showDeleteDialog.value = true
}

const confirmDelete = async () => {
  if (!itemToDelete.value || deletingItem.value) return
  deletingItem.value = true
  try {
    if (itemToDelete.value.type === 'folder') {
      await apiService.deleteFolder(itemToDelete.value.path)
      toast.add({ severity: 'success', summary: 'Успех', detail: 'Папка удалена' })
    } else {
      await apiService.deleteImage(itemToDelete.value.path)
      toast.add({ severity: 'success', summary: 'Успех', detail: 'Изображение удалено' })
    }
    showDeleteDialog.value = false
    itemToDelete.value = null
    refreshData()
  } catch (error) {
    console.error('Ошибка удаления:', error)
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось удалить' })
  } finally {
    deletingItem.value = false
  }
}

const downloadImage = (image) => {
  const link = document.createElement('a')
  link.href = image.url
  link.download = image.name
  link.click()
}

// ==================== Bulk operations ====================

const confirmBulkDelete = () => { showBulkDeleteDialog.value = true }

const performBulkDelete = async () => {
  deletingItems.value = true
  try {
    const paths = selectedItems.value.map(item => item.path)
    const result = await apiService.bulkDeleteItems(paths)
    toast.add({ severity: 'success', summary: 'Успех', detail: result.message })
    if (result.failed.length > 0) {
      toast.add({ severity: 'warn', summary: 'Предупреждение', detail: `Не удалось удалить ${result.failed.length} элементов` })
    }
    clearSelection()
    showBulkDeleteDialog.value = false
    refreshData()
  } catch (error) {
    console.error('Ошибка множественного удаления:', error)
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось удалить элементы' })
  } finally {
    deletingItems.value = false
  }
}

const exportSelected = async () => {
  if (selectedItems.value.length === 0 || exporting.value) return
  const imagePaths = selectedItems.value.filter(item => item.type === 'image').map(item => item.path)
  if (imagePaths.length === 0) {
    toast.add({ severity: 'info', summary: 'Нет изображений для экспорта', detail: 'Выберите хотя бы одно изображение', life: 3000 })
    return
  }
  try {
    exporting.value = true
    const blobData = await apiService.exportImages(imagePaths)
    const blob = new Blob([blobData], { type: 'application/zip' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
    link.href = url
    link.download = `images-export-${timestamp}.zip`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    toast.add({ severity: 'success', summary: 'Экспорт начат', detail: `Скачивание ${imagePaths.length} файлов`, life: 4000 })
  } catch (error) {
    console.error('Ошибка экспорта изображений:', error)
    toast.add({ severity: 'error', summary: 'Ошибка', detail: error.response?.data?.detail || error.message || 'Не удалось экспортировать изображения', life: 5000 })
  } finally {
    exporting.value = false
  }
}

async function moveItemsToFolder(items, targetPath) {
  try {
    const sourcePaths = items.map(item => item.path)
    const result = await apiService.moveItems(sourcePaths, targetPath)
    toast.add({ severity: 'success', summary: 'Успех', detail: result.message })
    if (result.failed.length > 0) {
      toast.add({ severity: 'warn', summary: 'Предупреждение', detail: `Не удалось переместить ${result.failed.length} элементов` })
    }
    clearSelection()
    refreshData()
  } catch (error) {
    console.error('Ошибка перемещения:', error)
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось переместить элементы' })
  }
}

// ==================== Resync ====================

const removeResyncSelection = (path) => {
  const index = resyncSelectedFolders.value.indexOf(path)
  if (index !== -1) {
    const next = [...resyncSelectedFolders.value]
    next.splice(index, 1)
    resyncSelectedFolders.value = next
  }
}

const startResync = async () => {
  if (resyncSelectedFolders.value.length === 0) {
    toast.add({ severity: 'warn', summary: 'Не выбраны папки', detail: 'Добавьте хотя бы одну папку для синхронизации', life: 4000 })
    return
  }
  resyncSubmitting.value = true
  try {
    const payload = { folders: resyncSelectedFolders.value, clear_existing: resyncClearExisting.value }
    const response = await apiService.resyncImageFolders(payload)
    const foldersCount = resyncSelectedFolders.value.length
    resyncSelectedFolders.value = []
    const taskId = response?.task_id
    toast.add({
      severity: 'success', summary: 'Задача запущена',
      detail: taskId ? `Синхронизация ${foldersCount} папок запущена (задача ${taskId}).` : `Синхронизация ${foldersCount} папок запущена.`,
      life: 6000
    })
    showResyncDialog.value = false
  } catch (error) {
    console.error('Ошибка запуска синхронизации папок:', error)
    toast.add({ severity: 'error', summary: 'Ошибка', detail: error?.response?.data?.detail || 'Не удалось запустить синхронизацию изображений', life: 5000 })
  } finally {
    resyncSubmitting.value = false
  }
}

// ==================== Context menu ====================

const showContextMenu = (event, item, type) => {
  const items = []
  const itemWithType = { ...item, type }

  if (!isSelected(item, type) && selectedItems.value.length > 0) clearSelection()
  if (!isSelected(item, type)) selectedItems.value.push(itemWithType)

  if (selectedItems.value.length > 1) {
    items.push(
      { label: `Удалить выбранные (${selectedItems.value.length})`, icon: 'pi pi-trash', command: () => confirmBulkDelete() },
      { separator: true },
      { label: 'Снять выделение', icon: 'pi pi-times', command: () => clearSelection() }
    )
  } else {
    if (type === 'folder') {
      items.push(
        { label: 'Открыть', icon: 'pi pi-folder-open', command: () => openFolder(itemWithType) },
        { label: 'Переименовать', icon: 'pi pi-pencil', command: () => renameItem(itemWithType) },
        { separator: true },
        { label: 'Удалить', icon: 'pi pi-trash', command: () => deleteItem(itemWithType) }
      )
    } else if (type === 'image') {
      items.push(
        { label: 'Просмотр', icon: 'pi pi-eye', command: () => openImage(itemWithType) },
        { label: 'Скачать', icon: 'pi pi-download', command: () => downloadImage(itemWithType) },
        { label: 'Переименовать', icon: 'pi pi-pencil', command: () => renameItem(itemWithType) },
        { separator: true },
        { label: 'Удалить', icon: 'pi pi-trash', command: () => deleteItem(itemWithType) }
      )
    }
  }
  contextMenuItems.value = items
  contextMenu.value.show(event)
}

// ==================== OS Drop zone ====================

function onPageDragEnter(e) {
  if (!e.dataTransfer.types.includes('Files')) return
  dragEnterCount++
  isPageDragOver.value = true
}

function onPageDragLeave() {
  dragEnterCount--
  if (dragEnterCount <= 0) { isPageDragOver.value = false; dragEnterCount = 0 }
}

function onPageDrop(e) {
  isPageDragOver.value = false
  dragEnterCount = 0
  const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'))
  if (!files.length) return
  selectedImages.value = files
  showUploadDialog.value = true
}

// ==================== Keyboard ====================

const handleKeydown = (event) => {
  if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName)) return
  switch (event.key) {
    case 'Delete':
    case 'Backspace':
      if (selectedItems.value.length > 0) {
        event.preventDefault()
        if (selectedItems.value.length === 1) deleteItem(selectedItems.value[0])
        else confirmBulkDelete()
      }
      break
    case 'a':
    case 'A':
      if (event.ctrlKey || event.metaKey) { event.preventDefault(); handleSelectAll() }
      break
    case 'Escape':
      if (selectedItems.value.length > 0) { event.preventDefault(); clearSelection() }
      break
  }
}


const visiblePages = computed(() => {
  const pages = []
  const total = totalPages.value
  const current = currentPage.value
  if (total <= MAX_VISIBLE_PAGES) {
    for (let i = 1; i <= total; i++) pages.push(i)
  } else {
    if (current <= 3) {
      for (let i = 1; i <= 4; i++) pages.push(i)
      if (total > 5) pages.push('...')
      pages.push(total)
    } else if (current >= total - 2) {
      pages.push(1)
      if (total > 5) pages.push('...')
      for (let i = total - 3; i <= total; i++) pages.push(i)
    } else {
      pages.push(1)
      pages.push('...')
      for (let i = current - 1; i <= current + 1; i++) pages.push(i)
      pages.push('...')
      pages.push(total)
    }
  }
  return pages
})

// ==================== Lifecycle ====================

onMounted(() => {
  loadData(true)
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
.images-page {
  padding: 1rem;
  position: relative;
}

.folder-item,
.image-item {
  border: 1px solid var(--surface-border);
  transition: all 0.2s;
  position: relative;
  user-select: none;
}

.selectable-item { cursor: pointer; }

.selectable-item.bg-primary-100 {
  border-color: var(--primary-color);
  background: var(--primary-50);
}

.folder-item:hover:not(.bg-primary-100),
.image-item:hover:not(.bg-primary-100) {
  border-color: var(--primary-color);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.image-thumbnail {
  border: 1px solid var(--surface-border);
  border-radius: 4px;
  overflow: hidden;
  width: 100%;
  height: var(--thumb-size, 120px);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background: var(--surface-50);
}

.image-thumbnail img {
  transition: transform 0.2s;
  opacity: 1;
}

.image-loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.image-item:hover .image-thumbnail img { transform: scale(1.05); }

/* Hover preview */
.image-hover-preview {
  display: none;
  position: absolute;
  z-index: 2000;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  width: 320px;
  padding: 4px;
  background: var(--surface-0);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.18);
  pointer-events: none;
}
.image-item:hover .image-hover-preview { display: block; }
.image-hover-preview img {
  width: 100%;
  max-height: 360px;
  object-fit: contain;
  border-radius: 6px;
}
@media (hover: none) { .image-hover-preview { display: none !important; } }
.image-item.preview-below .image-hover-preview {
  bottom: auto;
  top: calc(100% + 8px);
}

.selection-indicator {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  background: #10b981;
  border: 2px solid white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  color: white;
  font-size: 14px;
  font-weight: bold;
}

.selection-rectangle {
  position: absolute;
  border: 2px solid var(--primary-500);
  background: rgba(var(--primary-500), 0.15);
  pointer-events: none;
  z-index: 1000;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

/* Drag and drop */
.selectable-item[draggable="true"]:hover { cursor: grab; }
.selectable-item[draggable="true"]:active { cursor: grabbing; }

.drag-over {
  outline: 2px dashed var(--primary-color);
  outline-offset: -4px;
  filter: drop-shadow(0 2px 6px rgba(16,185,129,0.25));
  background: rgba(16,185,129,0.06);
}

.dragging {
  opacity: 0.75;
  transform: scale(0.98);
  transition: transform 120ms ease, opacity 120ms ease;
}

/* Animations */
.folder-item, .image-item {
  animation: fadeIn 0.3s ease-in;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Grid container */
.grid-container {
  position: relative;
  width: 100%;
  min-height: 400px;
  max-height: calc(100vh - 320px);
  overflow: auto;
  overscroll-behavior: contain;
}

/* List view */
.list-virtual-wrapper {
  border: 1px solid var(--surface-border);
  border-radius: 6px;
  overflow: hidden;
}

.list-header, .list-row {
  display: grid;
  grid-template-columns: 3rem 3rem minmax(0, 1fr) 10rem 14rem 5rem;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
}

.list-header {
  background: var(--surface-100);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--surface-600);
}

.list-virtual-scroller { max-height: calc(100vh - 360px); }

.list-row {
  border-bottom: 1px solid var(--surface-border);
  transition: background 0.2s ease;
}

.list-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem 1rem;
  color: var(--surface-600);
}

.list-row:hover { background: var(--surface-50); }
.list-row.is-selected { background: var(--primary-50); }

.list-cell {
  display: flex;
  align-items: center;
  min-width: 0;
}

.checkbox-cell, .icon-cell { justify-content: center; }

.name-cell span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.size-cell, .updated-cell, .actions-cell {
  justify-content: flex-end;
  font-size: 0.85rem;
  color: var(--surface-600);
}

.actions-cell { justify-content: center; }
.list-action { color: var(--surface-500); }

/* Resync chips */
.resync-selected-list {
  border: 1px solid var(--surface-border);
  border-radius: 6px;
  padding: 0.75rem;
}
.resync-selected-chips { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.resync-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: var(--primary-50);
  color: var(--primary-color);
  border: 1px solid var(--primary-200);
  border-radius: 999px;
  padding: 0.3rem 0.75rem;
  font-size: 0.85rem;
}
.resync-chip-remove { cursor: pointer; font-size: 0.85rem; color: inherit; }
.resync-chip-remove:hover { color: var(--primary-700); }

/* OS Drop zone */
.page-drop-zone {
  position: fixed;
  inset: 0;
  z-index: 5000;
  background: rgba(59, 130, 246, 0.08);
  border: 3px dashed var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
}
.drop-zone-content { text-align: center; color: var(--primary-color); }

/* FileUpload */
:deep(.p-fileupload-content) {
  border: 2px dashed var(--surface-border);
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
}
:deep(.p-fileupload-content:hover) { border-color: var(--primary-color); }

/* Scrollbar */
.grid-container::-webkit-scrollbar { width: 10px; height: 10px; }
.grid-container::-webkit-scrollbar-track { background: var(--surface-100); }
.grid-container::-webkit-scrollbar-thumb {
  background-color: var(--surface-400);
  border-radius: 6px;
  border: 2px solid var(--surface-100);
}
.grid-container { scrollbar-width: thin; scrollbar-color: var(--surface-400) var(--surface-100); }

.list-virtual-scroller ::-webkit-scrollbar { width: 10px; }
.list-virtual-scroller ::-webkit-scrollbar-thumb {
  background-color: var(--surface-400);
  border-radius: 6px;
}

/* 2K/4K screens */
@media (min-width: 1920px) {
  .grid-container .col-2xl-1 { width: 8.3333%; flex: 0 0 auto; }
  .grid-container .col-2xl-2 { width: 16.6667%; flex: 0 0 auto; }
}

/* Responsive */
@media (max-width: 768px) {
  .images-page { padding: 0.5rem; }
  .selection-indicator { top: 4px; right: 4px; width: 20px; height: 20px; }
}

.text-overflow-ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
